/* Not "jiffy-api" because it located in the same directory */
import {
  destroy,
  get,
  nonResponsePost,
  patch,
  persistentPatch,
  post,
  put
} from "common/utils/request/methods";
import { makeFullPath } from "common/utils/request/utils";
import routes from "config/routes";

const supportsKeepAlive = typeof Request !== "undefined" && "keepalive" in new Request("");
const supportsSendBeacon = "sendBeacon" in navigator;
const featureUsageUpdate = path => {
  if (supportsKeepAlive) {
    return persistentPatch(path);
  }
  if (supportsSendBeacon) {
    return nonResponsePost(path);
  }
  return patch(path);
};

/* eslint-disable max-lines */
export default {
  cart: {
    delivery: get(makeFullPath("/api/delivery")),
    removeLineItem: destroy(makeFullPath("/cart/:orderNumber/line_items/:lineItemId")),
    /** @type {(params: FormData | import('./models').cart.madeAdd.Request, options: unknown) => Promise<import('./models').cart.madeAdd.Response>} */
    madeAdd: post(makeFullPath("/api/cart/made_add")),
    matchingVariants: get(makeFullPath("/api/line_items/:lineItemId/matching_variants.json")),
    /** @type {(params: FormData | import('./models').cart.madeReorder.Request, options: unknown) => Promise<import('./models').cart.madeReorder.Response>} */
    madeReorder: post(makeFullPath("/api/cart/made_reorder"))
  },
  canva: {
    /** @type {() => Promise<import('./models').canva.designs.Response>} */
    designs: get(makeFullPath("/canva/designs")),
    /** @type {(params: import('./models').canva.designStatus.Request) => Promise<import('./models').canva.designStatus.Response>} */
    designStatus: get(makeFullPath("/canva/designs/:jobId")),
    /** @type {(params: import('./models').canva.selectDesign.Request) => Promise<import('./models').canva.selectDesign.Response>} */
    selectDesign: post(makeFullPath("/canva/designs/:designId")),
    /** @type {(params: import('./models').canva.settings.Request) => Promise<import('./models').canva.settings.Response>} */
    settings: get(makeFullPath("/canva/settings"))
  },
  productCollections: {
    cart: get(makeFullPath("/threads/cart")),
    createIntent: post(
      makeFullPath(
        "/threads/stripe/create_intent?payment_method_type=card&payment_method_id=:paymentMethodId"
      )
    ),
    // it returns delivery dates for entire cart and it checks available Inventory too
    oosNotify: post(makeFullPath("/threads/product_bundles/:id/favorite_product_bundles")),
    delivery: get(
      makeFullPath(
        "/api/v2/cart/delivery.json?cb=:cb&jiffy_classics_order=true&order_number=:order_number"
      )
    ),
    paymentMethods: get(makeFullPath("/threads/stripe/payment_methods")),
    // it returns estimated delivery date for bundle for given size
    pdpDelivery: post(
      makeFullPath("/api/products/:bundle_id/delivery_estimates/collections_pdp_date")
    ),
    price: post(makeFullPath("/threads/product_bundles/:id/price")),
    updateCartItem: patch(makeFullPath("/threads/cart")),
    productReviews: get(makeFullPath("/threads/product_bundles/:id/reviews")),
    updateAddress: patch(makeFullPath("/api/orders/:order_number/update_threads_checkout_address")),
    updateExpressCheckoutAddress: patch(
      makeFullPath("/api/orders/:order_number/express_checkout_address_change")
    ),
    createExpressCheckoutOrder: post(
      makeFullPath("/api/orders/create_threads_express_checkout_order")
    ),
    updateSetupFutureUsage: patch(
      makeFullPath("/threads/stripe/update_setup_future_usage?payment_intent_id=:paymentIntentId")
    )
  },

  checkoutV2: {
    createIntent: post(
      makeFullPath(
        "/:businessLine/stripe/payment_intents?payment_method_type=card&payment_method_id=:paymentMethodId&reassign=:reassign"
      )
    ),
    paymentMethods: get(makeFullPath("/:businessLine/stripe/payment_methods")),
    updateSetupFutureUsage: patch(
      makeFullPath(
        "/:businessLine/stripe/update_setup_future_usage?payment_intent_id=:paymentIntentId"
      )
    ),
    cart: get(makeFullPath("/:businessLine/cart")),
    delivery: get(
      makeFullPath(
        "/api/v2/cart/delivery.json?cb=:cb&order_number=:order_number&order_type=:order_type"
      )
    )
  },

  print: {
    delivery: get(makeFullPath("/api/v2/cart/delivery.json?cb=:cb&print=true")),
    /** @type {(params: import('./models').print.estimatedDelivery.Request) => Promise<import('./models').print.estimatedDelivery.Response>} */
    estimatedDelivery: get(
      makeFullPath("/api/made/current_pdp_date?composition_id=:composition_id")
    )
  },

  vueCart: {
    delivery: get(makeFullPath("/api/v2/cart/delivery.json?cb=:cb")),
    getCartState: get(makeFullPath("/api/v2/cart.json?cb=:cb")),
    getCartVariants: get(
      makeFullPath("/api/v2/cart/variants.json?composition_id=:id&placement_count=:count")
    ),
    updateVariantQuantities: patch(makeFullPath("/api/v2/cart/made_update.json")),
    removeCouponCode: destroy(makeFullPath("/api/v2/cart/remove_coupon_code.json?reason=:reason")),
    removeLineItem: patch(makeFullPath("/api/v2/cart.json")),
    setCouponCode: post(makeFullPath("/api/v2/cart/apply_coupon_code.json")),
    setShipAddress: put(makeFullPath("/api/v2/cart/set_ship_address.json")),
    updateLineItem: patch(makeFullPath("/api/v2/cart.json")),
    updateLineItems: patch(makeFullPath("/api/v2/cart/line_items.json")),
    setUseStoreCredits: patch(routes.setUseStoreCreditsPath()),
    currentItems: get(makeFullPath("/api/v2/cart/current_items.json")),
    preCut: patch(makeFullPath("/api/v2/cart/pre_cut.json")),
    updateDtfSize: patch(makeFullPath("/api/v2/cart/update_dtf_size.json")),
    addDtfSize: post(makeFullPath("/api/v2/cart/add_dtf_size.json")),
    fetchMatchingProduct: get(makeFullPath("/api/v2/cart/fetch_matching_product.json"))
  },
  blanksOrder: {
    update: put(makeFullPath("/api/cart/blanks_order")),
    removeLineItem: destroy(makeFullPath("/api/cart/blanks_order/line_items/:lineItemId")),
    outOfStock: get(makeFullPath("/api/cart/blanks_order/out_of_stock"))
  },
  product: {
    popular: get(routes.popularProductsPath()),
    currentPrices: get(routes.currentPricesPath()),
    compositionsPath: get(routes.colorPickerCompositionsPath()),
    show_product_grid: get("/products/:productId/show_product_grid"),
    fetchSimilarItemsButtonHtml: get(routes.similarItemsButtonPath()),
    fetchSimilarProductsHtml: get(routes.similarProductsPath()),
    sendSocialMediaConversionApi: post(routes.productSocialMediaConversionApiPath()),
    fetchMatchingProductsHtml: get(routes.matchingProductsPath()),
    fetchDeliveryEstimates: get(routes.pdpDeliveryEstimatesPath()),
    fetchDeliveryEstimatesForCurrentCart: post(routes.pdpDeliveryEstimatesForCurrentCartPath()),
    fetchWithColorMobileGrid: get(routes.pdpMobileGridWithColorPath()),
    fetchCompositionEstimates: get(routes.compositionEstimationDatePath()),
    fetchFlashCompositionEstimates: get(
      makeFullPath("/api/v2/estimates/flash_composition_dates.json")
    ),
    fetchFlashCommonDates: get(makeFullPath("/api/v2/estimates/flash_made_dates.json")),
    fetchBusinessDeliveryDates: get(makeFullPath("/api/v2/:business_line/delivery_estimates.json")),
    fetchCachedDeliveryDate: get(makeFullPath("/api/v2/estimates/cached_delivery_date.json")),
    fetchDeliverToModal: get(routes.deliverToModalPath()),
    fetchDynamicJavascripts: get(routes.dynamicJavascriptsPath()), // GET for backward compatibility & small payloads
    fetchDynamicJavascriptsPost: post(routes.dynamicJavascriptsPath()), // POST for large payloads to avoid 413
    fetchMoreDecoratorReviews: get(routes.moreDecoratorReviewsPath()),
    fetchProductReviews: get(routes.productReviewsPath()),
    popupAddToCartPath: get(routes.popupAddToCartPath()),
    popupColorPickerPath: get(routes.popupColorPickerPath()),
    /** @type {(params: import('./models').product.cp.Request) => Promise<import('./models').product.cp.Response>} */
    cp: get(makeFullPath("/api/products/:productId/cp?design_lab=true&made=true&print=:printMode"))
  },
  favorites: {
    getFavoriteItems: get(routes.getFavoriteItemsPath()),
    addFavoriteItem: post(routes.addFavoriteItemPath()),
    removeFavoriteItem: destroy(routes.removeFavoriteItemPath()),
    unsubscribeFavoriteItem: patch(routes.unsubscribeFavoriteItemPath()),
    updateFavoriteNote: patch(routes.updateNoteFavoriteItemPath()),
    subscribe: post(makeFullPath("/api/subscriptions"))
  },
  featureUsage: {
    update: featureUsageUpdate(routes.updateOrderFeatureUsagePath())
  },
  location: {
    search: get(makeFullPath("/api/locations.json")),
    update: post(makeFullPath("/api/locations.json"))
  },
  locations: {
    create: post(makeFullPath("/api/locations.json")),
    update: put(makeFullPath("/api/locations/:id")),
    delete: destroy(makeFullPath("/api/locations/:zipcode")),
    getCurrentLocation: get(makeFullPath("/api/locations/current_location?cb=:cb"))
  },
  proofs: {
    save: post(makeFullPath("/printer/proofs/:proofId")),
    submit: post(makeFullPath("/printer/proofs/:proofId?submit_proof=true")),
    submitNew: post(makeFullPath("/printer/proofs?submit_proof=true")),
    create: post(makeFullPath("/printer/proofs"))
  },
  refunds: {
    create: post(makeFullPath("/refunds"))
  },
  review: {
    save: post(makeFullPath("/printers/:token/reviews"))
  },
  conversation: {
    read: post(makeFullPath("/mark_conversation_read/:conversationId"))
  },
  portfolioImages: {
    rotate: post(
      makeFullPath("/yourshop/portfolio_images/:imageId/rotate.json?designer_id=:designerId")
    ),
    upload: post(makeFullPath("/api/portfolio_images"))
  },
  imageConversions: {
    rasterize: post(makeFullPath("/api/image_conversion/rasterize"))
  },
  geolocations: {
    zipcode: get("/print/api/geolocation/zipcode")
  },
  bidsRequests: {
    create: post(makeFullPath("/bids_requests"))
  },
  projects: {
    update: patch(makeFullPath("/api/project")),
    removeDesigner: destroy(makeFullPath("/api/project/remove_designer")),
    checkout: post(makeFullPath("/api/project/checkout")),
    selectDesigner: post(makeFullPath("/api/project/select_designer")),
    updateZipcode: post(makeFullPath("/api/project/update_zipcode"))
  },
  artworks: {
    create: post(makeFullPath("/api/project/artworks")),
    update: put(makeFullPath("/api/project/artworks/:id")),
    destroy: destroy(makeFullPath("/api/project/artworks/:id"))
  },
  printerSignup: {
    emailUser: get(makeFullPath("/printer/signup/email_user"))
  },
  storePreference: {
    post: post("api/store_preference")
  },
  storefrontPortfolio: {
    update: put(makeFullPath("/printer/storefront/portfolio")),
    reorder: put(makeFullPath("/api/portfolio_images/reorder")),
    destroy: destroy(makeFullPath("/api/portfolio_images/:id"))
  },
  printerStorefront: {
    uploadLogo: put(makeFullPath("/printer/logo"))
  },
  printers: {
    index: get(makeFullPath("/printers"))
  },
  printer: {
    requestGreenscreen: post(makeFullPath("/api/greenscreen_request"))
  },
  pricingCalculations: {
    get: get(makeFullPath("/api/printers/:printerId/pricing_calculations")),
    default: get(makeFullPath("/api/pricing_calculations"))
  },
  customerFunnel: {
    interview: {
      update: put(makeFullPath("/project/project-create"))
    },
    artwork: {
      update: put(makeFullPath("/project/artwork"))
    }
  },
  jiffyshirts: {
    survey: {
      store: post(makeFullPath("/surveys/attempts"))
    },
    cart: {
      /** @type {() => Promise<import('./models').jiffyshirts.cart.quantity.Response>} */
      quantity: get(makeFullPath("/api/cart/quantity")),
      searchQuotes: get(routes.searchQuotesPath()),
      runQuoteAssignment: get(routes.runQuoteAssignmentPath()),
      rateBotMessage: post(routes.rateBotMessagePath()),
      updateAiMessage: patch(routes.updateAiMessagePath()),
      getCartUpsell: get(makeFullPath("/api/v2/cart/upsell.json?catalog_number=:catalog_number"))
    },
    preference: post(makeFullPath("/account/update_preference")),
    facets: get(makeFullPath("/api/facets")),
    facetsFilters: get(makeFullPath("/api/facets/filters")),
    validateAddress: post(makeFullPath("/api/addresses/validate")),
    locations: {
      create: post(makeFullPath("/api/locations")),
      update: put(makeFullPath("/api/locations/:id"))
    },
    orders: {
      uploadShippingList: post(makeFullPath("/api/orders/upload_shipping_list")),
      removeShippingList: destroy(makeFullPath("/api/orders/remove_shipping_list")),
      setShippingSpeed: put(makeFullPath(`/api/orders/:order_number/set_shipping_speed`)),
      updatePaypalSummary: patch(makeFullPath("/api/orders/:order_number/paypal_summary")),
      createPaypalOrder: post(makeFullPath("/api/orders/:order_number/paypal_order")),
      updateApplePayAddressAndRecalculate: post(
        makeFullPath(`/api/orders/:order_number/update_apple_pay_address`)
      ),
      googlePaySummary: patch(makeFullPath(`/api/orders/:order_number/google_pay_summary`)),
      applePayLoad: post(makeFullPath(`/api/orders/:order_number/current_apple_pay_payload`))
    },
    checkout: {
      update: patch(routes.updateCheckoutPath()),
      prepareKlarnaOrder: put(routes.prepareKlarnaOrderPath()),
      generateBraintreeToken: post(routes.generateBraintreeTokenPath()),
      thankYou: get(routes.thankYouPath()),
      submitPayment: patch(makeFullPath("/checkout/update/payment")),
      log: post(routes.loggerCheckoutPath()),
      stripeCreateIntent: post(routes.stripeCreateIntentPath()),
      startSezzleCheckout: post(routes.startSezzleCheckoutPath()),
      crossSellTransfersStoreCredit: get(routes.crossSellTransfersStoreCreditPath()),
      orderSurveys: post(makeFullPath("/api/v2/order_surveys.json"))
    },
    carousel: {
      getCarouselImages: get("/api/product_images/:slug?made_dedicated_pdp=:made_dedicated_pdp")
    },
    product: {
      /** @type {(params: import('./models').jiffyshirts.product.inventory.Request, queryParams?: import('./models').jiffyshirts.product.inventory.QueryParams) => Promise<import('./models').jiffyshirts.product.inventory.Response>} */
      inventory: get(makeFullPath("/api/products/:slug/inv")),
      comments: get(makeFullPath("/api/products/:id/comments")),
      addComment: post(makeFullPath("/api/products/:id/comments")),
      voteComment: post("/api/products/comments/votes"),
      search: get(routes.apiSearchProductsPath()),
      sizesWithColors: get(routes.productSizesColorsPath()),
      adminSizesWithColors: get(routes.adminProductSizesColorsPath())
    },
    trackEngagement: {
      product: post(makeFullPath("/api/track_engagement/product")),
      processedArtwork: post(makeFullPath("/api/track_engagement/processed_artwork"))
    },
    savedCart: {
      list: get(makeFullPath("/api/v2/saved_carts.json")),
      current: get(makeFullPath("/api/v2/saved_carts/active.json")),
      create: post(makeFullPath("/api/v2/saved_carts.json")),
      update: put(makeFullPath("/api/v2/saved_carts/:id.json")),
      switch: put(makeFullPath("/api/v2/saved_carts/:id/switch.json")),
      add: post(makeFullPath("/api/cart/add"))
    },
    rebrand: {
      getRedirectUrl: post(makeFullPath("/api/user_tokens"))
    }
  },
  chat: {
    chats: get(routes.chatGptChatsPath()),
    session: get(routes.chatGptSessionPath()),
    delete: destroy(routes.chatGptDeleteChatPath())
  },
  account: {
    auth: {
      signIn: post(makeFullPath("/api/v2/sign_in.json")),
      signUp: post(makeFullPath("/api/v2/sign_up.json"))
    },
    addresses: {
      removeOne: destroy(makeFullPath("/api/orders/:order_number/delete_ship_address")),
      setOne: put(makeFullPath("/api/orders/:order_number/set_ship_address")),
      getAll: get(makeFullPath("/api/account/addresses")),
      create: post(makeFullPath("/account/addresses")),
      getAddress: get(makeFullPath("/api/addresses")),
      createAddress: post(makeFullPath("/api/addresses")),
      updateAddress: patch(makeFullPath("/api/addresses/:address_id"))
    },
    shipments: {
      locations: get(makeFullPath("/api/account/orders/shipments/:shipment_id/locations"))
    },
    taxes: {
      removeCertificate: destroy(makeFullPath("/tax_exempt/remove_complete_form"))
    },
    savedCarts: {
      getAssignmentData: get(routes.savedCartsAssignmentPath()),
      switch: put(routes.switchSavedCartPath()),
      create: post(routes.savedCartsPath()),
      recalculateSavings: get(routes.savedCartsRecalculatePath())
    },
    oauthConnects: {
      getEtsyImportStatus: get(routes.etsyConnectedPath()),
      getShopifyImportStatus: get(routes.shopifyConnectedPath()),
      getSquareImportStatus: get(routes.squareConnectedPath()),
      getWoocommerceImportStatus: get(routes.woocommerceConnectedPath())
    },
    notificationSetting: {
      update: put(routes.updateNotificationSettingPath())
    },
    paymentMethods: {
      create: post("api/account/payment_methods"),
      createSetupIntent: get(makeFullPath("/account/payment_methods/:cardId/create_setup_intent")),
      getBraintreeClientToken: get(
        makeFullPath("/account/payment_methods/:cardId/get_braintree_client_token")
      )
    },
    twoFactorAuth: {
      sendVerification: post(routes.sendVerificationPath()),
      verifyAccountChanges: post(routes.verifyAccountChangesPath())
    },
    promotions: {
      /** @type {() => Promise<import('./models').account.promotions.count.Response>} */
      count: get(makeFullPath("/api/account/promotions/count"))
    }
  },
  consumers: {
    list: get(routes.consumersPath()),
    create: post(routes.consumersPath()),
    update: patch(routes.consumerPath()),
    delete: destroy(routes.consumerPath()),
    attachToOrder: post(routes.attachConsumerToOrderPath()),
    detachFromOrder: post(routes.detachConsumerFromOrderPath()),
    updateAddress: patch(routes.consumerAddressPath()),
    createAddress: post(routes.consumerAddressPath())
  },
  merchantListings: {
    unmappedLineItems: get(routes.unmappedLineItemsPath()),
    sync: patch(routes.syncMerchantListingsPath()),
    unassigned: get(routes.unassignedMerchantListingsPath()),
    undoAssign: patch(routes.undoAssignMerchantListingsPath()),
    update: patch(routes.merchantListingsPath()),
    variants: {
      list: get(routes.merchantVariantsPath()),
      update: patch(routes.updateMerchantVariantPath()),
      updateBulk: patch(routes.bulkUpdateMerchantVariantPath()),
      // saveKeyword: patch(routes.saveEtsyKeywordPath()),
      adminList: get(routes.adminMerchantVariantsPath()),
      adminUpdate: patch(routes.adminUpdateMerchantVariantPath()),
      adminUpdateBulk: patch(routes.adminBulkUpdateMerchantVariantPath())
    }
  },
  websiteNotifications: {
    index: get(routes.websiteNotificationsList()),
    update: patch(routes.updateWebsiteNotification())
  },
  menu: {
    fetchMobileMenu: get(routes.mobileMenuItemsPath()),
    fetchMobileBanner: get(routes.mobileMenuBannerPath())
  },
  user: {
    current: post(makeFullPath("/api/users/current"))
  },
  dtf: {
    getArtwork: get(makeFullPath("/api/users/dtf_artworks/:id")),
    getArtworksFromOrder: get(
      makeFullPath("/api/users/dtf_artworks/dtf_artworks_from_order/:order_number")
    ),
    /** @type {(formData: FormData, options: unknown) => Promise<import('./models').dtf.uploadArtwork.Response>} */
    uploadArtwork: post(makeFullPath("/api/users/dtf_artworks")),
    stickerCutlines: get(makeFullPath("/api/users/dtf_artworks/:id/sticker_cutlines")),
    updateArtworkStyle: post(makeFullPath("/api/users/dtf_artworks/:id/update_artwork_style")),
    isNewDtfCustomer: get(makeFullPath("/api/users/dtf_artworks/is_new_dtf_customer")),
    bogoRecommendedBlanks: get(makeFullPath("/api/products/bogo-recommendations.json")),
    isNewToDtfForBogo: get(makeFullPath("/api/users/dtf_artworks/is_new_to_dtf_for_bogo")),
    pdpCtaSalePrice: post(makeFullPath("/api/products/dtf_pdp_cta_sale_price")),
    dtfPricing: post(makeFullPath("/api/products/:product/dtf_pricing")),
    addToCart: post(makeFullPath("/api/cart/dtf_add")),
    addToCartPrint: post(makeFullPath("/api/cart/print_reorder")),
    /** @type {(data: {catalog_number: string}) => Promise<import('./models').dtf.getInitConfig.Response>} */
    getInitConfig: get(makeFullPath("/api/dtf/init_config")),
    /** @type {(params: import('./models').dtf.getArtworkHistory.Request) => Promise<import('./models').dtf.getArtworkHistoryDetails.Response>} */
    getArtworkHistory: get(makeFullPath("/api/users/dtf_artworks/details")),
    /** @type {(params: import('./models').dtf.getArtworkHistory.Request) => Promise<import('./models').dtf.getArtworkHistory.Response>} */
    getArtworkHistories: get(makeFullPath("/api/users/dtf_artworks/list")),
    getSpangleVariation: post(makeFullPath("/api/users/dtf_artworks/:id/spangle")),
    updateSpangleColor: post(makeFullPath("/api/users/dtf_artworks/change_color")),
    getStickersQuantities: post(makeFullPath("/api/stickers/prices/quantities")),
    getStickersPrices: post(makeFullPath("/api/stickers/prices")),
    generateCmykImages: post(makeFullPath("/api/users/dtf_artworks/:id/generate_cmyk_images")),
    generateSpangleImage: post(makeFullPath("/api/users/dtf_artworks/:id/generate_spangle_image")),
    cancelArtworkProcess: post(makeFullPath("/api/users/dtf_artworks/:id/cancel_artwork_process")),
    generateLettersPreview: post(makeFullPath("/api/dtf/generate_letters_preview")),
    generateLettersSizes: post(makeFullPath("/api/dtf/generate_letters_sizes")),
    createMailChimpEntry: post(makeFullPath("/api/dtf/create_mailchimp_entry")),
    segmentImage: post(makeFullPath("/api/users/dtf_artworks/segment_image")),
    toggleSegmentVersion: post(makeFullPath("/api/users/dtf_artworks/toggle_segment_version")),
    updateArtworkRedrawData: post(
      makeFullPath("/api/users/dtf_artworks/:id/update_artwork_redraw_data")
    ),
    addToCartGangSheet: post(makeFullPath("/api/users/dtf_artworks/add_to_cart_gang_sheet")),
    fetchBulkPrice: get(makeFullPath("/api/users/dtf_artworks/fetch_bulk_price")),
    // Manual background removal refinement endpoints
    /** @type {(
     *   pathParams: import('./models').dtf.manualRefine.completeSession.PathParams,
     *   body?: import('./models').dtf.manualRefine.completeSession.RequestBody | FormData,
     *   options?: unknown
     * ) => Promise<import('./models').dtf.manualRefine.completeSession.Response>} */
    completeRefineSession: post(
      makeFullPath("/api/users/dtf_artworks/:dtf_artwork_id/refine/complete")
    ),
    /** @type {(params: import('./models').dtf.acknowledgeArtwork.PathParams) => Promise<import('./models').dtf.acknowledgeArtwork.Response>} */
    acknowledgeArtwork: post(makeFullPath("/api/users/dtf_artworks/:id/acknowledge"))
  },
  dtfPrint: {
    updateRedrawData: patch(makeFullPath("/api/users/dtf_prints/:id"))
  },
  borneo: {
    uploadAsset: post(routes.borneoAssetUploadPath())
  },
  brands: {
    getProductCompositions: get(makeFullPath("/brands/:brand_name/cms/compositions")),
    getPagesOrder: get(makeFullPath("/brands/:brand_name/cms/pages_order")),
    updatePagesOrder: patch(makeFullPath("/brands/:brand_name/cms/pages_order")),
    followPage: post(makeFullPath("/brand_followers")),
    searchProducts: get(makeFullPath("/brands/:brand_name/cms/search_product")),
    unfollowPage: destroy(makeFullPath("/brand_followers/:brand_id")),
    updateCmsPage: put(makeFullPath("/brands/:brand_name/cms")),
    updateCmsPageSlug: put(makeFullPath("/brands/:brand_name/cms/pages/:slug")),
    unpublishCmsPage: destroy(makeFullPath("/brands/:brand_name/cms/publish_requests")),
    updateProductSection: put(makeFullPath("/brands/:brand_name/cms/pages/:slug/product_section"))
  },
  ordersClaims: {
    uploadImages: post(routes.claimsUploadImagesPath()),
    cancelComplete: post(routes.claimsCancelCompletePath()),
    reorderComplete: post(routes.claimsReorderCompletePath())
  },
  printOnDemand: {
    currentPodOrder: get(makeFullPath("/api/made/current_order")),
    updatePodOrder: put(makeFullPath("/api/made/current_order")),
    getProducts: get(makeFullPath("/api/made/products")),
    /** @type {(params: import('./models').printOnDemand.getProduct.Request, options?: import('./models').printOnDemand.getProduct.Options) => Promise<import('./models').printOnDemand.getProduct.Response>} */
    getProduct: get(makeFullPath("/api/made/products/:catalog_number")),
    getPrintableAreas: get(makeFullPath("/api/made/printable_areas")),
    getLivestylePreviewUrl: get(makeFullPath("/api/made/placements/:placement_id/preview_url")),
    getCompositionPrices: get(makeFullPath("/api/made/compositions/:id/prices")),
    getFlashStock: get(makeFullPath("/api/pdp/:slug/flash_inventory")),
    getFlashCompositions: get(makeFullPath("/api/pdp/:slug/flash_compositions")),
    getMadePreset: get(makeFullPath("/api/made/presets")),
    createMadePreset: post(makeFullPath("/api/made/presets/:side")),
    updateMadePreset: patch(makeFullPath("/api/made/presets/:side")),
    removeMadePreset: destroy(makeFullPath("/api/made/presets/:side")),
    getMadeOrder: get(makeFullPath("/api/pdp/:asgid/made_edit")),
    /** @type {(params: FormData | import('./models').printOnDemand.madeUpdate.Request, options: unknown) => Promise<import('./models').printOnDemand.madeUpdate.Response>} */
    updateMadeOrder: put(makeFullPath("/api/pdp/:asgid/made_update"))
  },
  designLab: {
    index: get(makeFullPath("/design-lab")),
    start: post(makeFullPath("/design-lab/start")),
    /** @type {(params: import('./models').designLab.getProjectPrices.Request) => Promise<import('./models').designLab.getProjectPrices.Response>} */
    getProjectPrices: post("/api/made/design_projects/prices"),
    /** @type {(pathParams: {slug: string}, body: {delivery_estimate: Omit<import('./models').designLab.getPdpDeliveryEstimates.Request, 'slug'>}) => Promise<import('./models').designLab.getPdpDeliveryEstimates.Response>} */
    getPdpDeliveryEstimates: post(
      makeFullPath("/api/products/:slug/delivery_estimates/current_pdp_date")
    ),
    /** @type {(params?: {sample?: boolean}) => Promise<import('./models').designLab.getLatestDesignProject.Response>} */
    getLatestDesignProject: get("/api/made/design_projects/latest"),
    /** @type {(params?: {sort_by?: string}) => Promise<import('./models').designLab.getDesignProjectsList.Response>} */
    getDesignProjectsList: get("/api/made/design_projects"),
    updateDesignProject: put("/api/made/design_projects/:id"),
    /** @type {(params: {uuid: string}) => Promise<{success: boolean}>} */
    deleteDesignProject: destroy("/api/made/design_projects/:uuid"),
    /** @type {(params: import('./models').designLab.getMadeEdit.Request) => Promise<import('./models').designLab.getMadeEdit.Response>} */
    getMadeEdit: get(makeFullPath("/api/pdp/:asgid/made_edit")),
    /** @type {(params?: import('./models').designLab.quantity.Request) => Promise<import('./models').designLab.quantity.Response>} */
    quantity: get(makeFullPath("/api/design_labs/quantity", { skipDekeo: true })),
    pinning: {
      /** @type {(params: import('./models').designLab.pinning.createSession.Request) => Promise<import('./models').designLab.pinning.createSession.Response>} */
      createSession: post(makeFullPath("/api/pin_sessions")),
      /** @type {(params: { id: string }) => Promise<import('./models').designLab.pinning.getSession.Response>} */
      getSession: get(makeFullPath("/api/pin_sessions/:id")),
      /** @type {(params: { id: string }) => Promise<import('./models').designLab.pinning.getPins.Response>} */
      getPins: get(makeFullPath("/api/pin_sessions/:id/pins")),
      /** @type {(params: { id: string } & import('./models').designLab.pinning.submitMessage.Request) => Promise<void>} */
      submitMessage: post(makeFullPath("/api/pin_sessions/:id/message")),
      /** @type {(urlParams: { id: string }, body: import('./models').designLab.pinning.createFeedback.Request) => Promise<void>} */
      createFeedback: post(makeFullPath("/api/pin_sessions/:id/feedback")),
      /** @type {(params: { id: string }) => Promise<import('./models').designLab.pinning.completeSession.Response>} */
      completeSession: post(makeFullPath("/api/pin_sessions/:id/complete")),
      /** @type {(params: { id: string }) => Promise<import('./models').designLab.pinning.cancelSession.Response>} */
      cancelSession: post(makeFullPath("/api/pin_sessions/:id/cancel")),
      /** @type {(params: { id: string, pinId: string }) => Promise<import('./models').designLab.pinning.removePin.Response>} */
      removePin: destroy(makeFullPath("/api/pin_sessions/:id/pins/:pinId")),
      /** @type {(urlParams: { id: string }, body: { pinIds: string[] }) => Promise<void>} */
      bulkDeletePins: post(makeFullPath("/api/pin_sessions/:id/pins/bulk_delete")),
      /** @type {(urlParams: { id: string, pinId: string }, body: import('./models').designLab.pinning.updatePinInstruction.Request) => Promise<import('./models').designLab.pinning.updatePinInstruction.Response>} */
      updatePinInstruction: patch(makeFullPath("/api/pin_sessions/:id/pins/:pinId"))
    }
  },
  made: {
    categorySections: {
      checkChanges: get(makeFullPath("/api/made/category_sections/check_changes"))
    }
  },
  bulkOrder: {
    getSuppliers: get(makeFullPath(routes.getSuppliersBulkOrderPath()))
  },
  llmRedrawRequests: {
    create: post(makeFullPath("/api/llm_redraw_requests")),
    update: patch(makeFullPath("/api/llm_redraw_requests/:id"))
  },
  neckLabels: {
    styles: get(makeFullPath("/api/neck_labels/styles")),
    styleDetail: get(makeFullPath("/api/neck_labels/styles/:id")),
    createStyle: post(makeFullPath("/api/neck_labels/styles")),
    deleteStyle: destroy(makeFullPath("/api/neck_labels/styles/:id"))
  },
  aiImages: {
    /** @type {(params: import('./models').aiImages.generate.Request) => Promise<import('./models').aiImages.generate.Response>} */
    generate: post(makeFullPath("/api/ai_images/generate")),
    /** @type {(params: import('./models').aiImages.refine.Request, options?: unknown) => Promise<import('./models').aiImages.refine.Response>} */
    refine: post(makeFullPath("/api/ai_images/refine"))
  },
  userPreferences: {
    /** @type {(params: import('./models').userPreferences.updateMockup.Request) => Promise<import('./models').userPreferences.updateMockup.Response>} */
    updateMockup: post(makeFullPath("/api/user_preferences/update_mockup"))
  },
  redrawnImageReview: {
    submit: post(makeFullPath("/redrawn_image_review/:token"))
  }
};
